import type { DashboardResponse } from '@/types/responses';

export type CrossSignalKind = 'velocity' | 'breakout' | 'sentiment' | 'hype' | 'momentum';

export interface CrossSignalEntry {
  kind: CrossSignalKind;
  label: string;
}

const TIME_SIGNAL_KINDS = new Set<CrossSignalKind>(['velocity', 'breakout', 'momentum']);

function formatPercentAbs(value: number): string {
  return `${Math.abs(value).toFixed(2)}%`;
}

export function computeCrossSignals(
  velocityAlerts: DashboardResponse['velocity_alerts'],
  breakouts: DashboardResponse['breakout_tickers'],
  sentimentShifts: DashboardResponse['sentiment_shift'],
  hypeScores: DashboardResponse['hype_score'],
  momentumTickers?: Set<string>,
  minHypeScore = 40
): Map<string, CrossSignalEntry[]> {
  const signals = new Map<string, CrossSignalEntry[]>();

  const add = (ticker: string, entry: CrossSignalEntry) => {
    const t = ticker.toUpperCase();
    if (!signals.has(t)) signals.set(t, []);
    signals.get(t)!.push(entry);
  };

  const seenVelocity = new Set<string>();
  velocityAlerts.forEach((a) => {
    const t = a.ticker.toUpperCase();
    if (!seenVelocity.has(t)) {
      seenVelocity.add(t);
      add(t, { kind: 'velocity', label: 'velocity spike' });
    }
  });

  breakouts.forEach((b) =>
    add(b.ticker.toUpperCase(), {
      kind: 'breakout',
      label: `breakout ${Number(b.velocity_increase).toFixed(1)}x`,
    })
  );

  sentimentShifts.forEach((s) => {
    const dir = s.sentiment_change >= 0 ? '▲' : '▼';
    add(s.ticker.toUpperCase(), {
      kind: 'sentiment',
      label: `sentiment ${dir}${formatPercentAbs(s.sentiment_change)}`,
    });
  });

  [...hypeScores]
    .sort((a, b) => (b.hype_score ?? 0) - (a.hype_score ?? 0))
    .slice(0, 10)
    .filter((s) => (s.hype_score ?? 0) >= minHypeScore)
    .forEach((s, i) =>
      add(s.ticker.toUpperCase(), {
        kind: 'hype',
        label: `hype #${i + 1}`,
      })
    );

  momentumTickers?.forEach((t) =>
    add(t.toUpperCase(), {
      kind: 'momentum',
      label: 'momentum',
    })
  );

  for (const [ticker, sigs] of signals.entries()) {
    if (sigs.length < 2) {
      signals.delete(ticker);
      continue;
    }
    const hasTimeSignal = sigs.some((s) => TIME_SIGNAL_KINDS.has(s.kind));
    if (!hasTimeSignal) {
      signals.delete(ticker);
    }
  }

  return signals;
}
